<template>
    <div class="tw-grid tw-gap-8 md:tw-grid-cols-5">
        <!-- Order summary -->
        <aside class="md:tw-col-span-2" aria-labelledby="summary-heading">
            <!-- CORVO-2065: an individual purchaser sees their actual receipt,
                 never the generic offer price. -->
            <div v-if="alreadyActive && activePurchase" class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-6">
                <h2 id="summary-heading" class="tw-text-lg tw-font-semibold tw-text-gray-900">Your purchase</h2>
                <div class="tw-mt-4 tw-flex tw-items-start tw-justify-between tw-border-b tw-border-gray-100 tw-pb-4">
                    <div>
                        <p class="tw-font-medium tw-text-gray-900">Career Readiness</p>
                        <p class="tw-text-sm tw-text-gray-500">Purchased {{ activePurchase.purchasedAt }}</p>
                    </div>
                    <p class="tw-font-semibold tw-text-gray-900">{{ formatMoney(activePurchase.total, activePurchase.currency) }}</p>
                </div>
                <dl class="tw-mt-4 tw-space-y-2 tw-text-sm">
                    <div class="tw-flex tw-justify-between">
                        <dt class="tw-text-gray-500">Subtotal</dt>
                        <dd class="tw-text-gray-900">{{ formatMoney(activePurchase.subtotal, activePurchase.currency) }}</dd>
                    </div>
                    <div class="tw-flex tw-justify-between">
                        <dt class="tw-text-gray-500">Sales tax</dt>
                        <dd class="tw-text-gray-900">{{ formatMoney(activePurchase.tax, activePurchase.currency) }}</dd>
                    </div>
                    <div class="tw-flex tw-justify-between tw-border-t tw-border-gray-100 tw-pt-2 tw-text-base tw-font-semibold">
                        <dt class="tw-text-gray-900">Total paid</dt>
                        <dd class="tw-text-gray-900">{{ formatMoney(activePurchase.total, activePurchase.currency) }}</dd>
                    </div>
                </dl>
                <p v-if="activePurchase.cardLastFour" class="tw-mt-3 tw-text-sm tw-text-gray-500">
                    {{ cardLabel }}
                </p>
                <p v-if="activePurchase.expiresAt" class="tw-mt-1 tw-text-sm tw-text-gray-500">
                    Access through {{ activePurchase.expiresAt }}
                </p>
                <a
                    v-if="activePurchase.receiptUrl"
                    :href="activePurchase.receiptUrl"
                    target="_blank"
                    rel="noopener"
                    class="tw-mt-4 tw-inline-block tw-text-sm tw-font-medium tw-text-simucase-red hover:tw-underline"
                >
                    View receipt<span class="tw-sr-only"> (opens in a new tab)</span>
                </a>
            </div>

            <!-- Active access with no personal receipt: institution-provisioned,
                 or an individual membership predating the purchase ledger. Show
                 no dollar amounts either way. -->
            <div v-else-if="alreadyActive" class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-6">
                <h2 id="summary-heading" class="tw-text-lg tw-font-semibold tw-text-gray-900">Your access</h2>
                <p class="tw-mt-4 tw-text-sm tw-text-gray-600">
                    {{ providedByInstitution
                        ? 'Your Career Readiness access is provided through your institution.'
                        : 'You have active Career Readiness access.' }}
                </p>
            </div>

            <div v-else class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-6">
                <h2 id="summary-heading" class="tw-text-lg tw-font-semibold tw-text-gray-900">Order summary</h2>
                <div class="tw-mt-4 tw-flex tw-items-start tw-justify-between tw-border-b tw-border-gray-100 tw-pb-4">
                    <div>
                        <p class="tw-font-medium tw-text-gray-900">Career Readiness</p>
                        <!-- ECL-7148: the discount is named in the line item itself rather
                             than in a separate banner. The banner said "your active Clinical
                             Experience membership qualifies you", which stopped being true
                             once eligibility widened to anyone who has ever held CE. -->
                        <p class="tw-text-sm tw-text-gray-500">
                            {{ isDiscounted ? 'Discounted Annual Access' : 'Annual Access' }}
                        </p>
                    </div>
                    <p class="tw-font-semibold tw-text-gray-900">{{ formattedPrice }}</p>
                </div>

                <!-- Tax isn't known until the address is submitted and the invoice
                     is created. Keep this as a persistent aria-live region so the
                     swap from the placeholder note to the priced breakdown (with the
                     new total) is announced to screen readers. -->
                <div aria-live="polite" class="tw-mt-4">
                    <p v-if="!pricing" class="tw-text-xs tw-text-gray-500">
                        Applicable sales tax is calculated at payment based on your billing address.
                    </p>
                    <dl v-else class="tw-space-y-2 tw-text-sm">
                        <div class="tw-flex tw-justify-between">
                            <dt class="tw-text-gray-500">Subtotal</dt>
                            <dd class="tw-text-gray-900">{{ formattedSubtotal }}</dd>
                        </div>
                        <div class="tw-flex tw-justify-between">
                            <dt class="tw-text-gray-500">Sales tax</dt>
                            <dd class="tw-text-gray-900">{{ formattedTax }}</dd>
                        </div>
                        <div class="tw-flex tw-justify-between tw-border-t tw-border-gray-100 tw-pt-2 tw-text-base tw-font-semibold">
                            <dt class="tw-text-gray-900">Total</dt>
                            <dd class="tw-text-gray-900">{{ formattedTotal }}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </aside>

        <!-- Purchase form / states -->
        <section class="md:tw-col-span-3" aria-labelledby="purchase-heading">
            <h1 id="purchase-heading" class="tw-text-2xl tw-font-bold tw-text-gray-900">
                Get Career Readiness
            </h1>

            <!-- Already has active access -->
            <div v-if="alreadyActive" ref="activePanel" tabindex="-1" class="tw-mt-6 tw-rounded-lg tw-border tw-border-green-200 tw-bg-green-50 tw-p-6" role="status">
                <p class="tw-font-medium tw-text-green-900">You already have active Career Readiness access.</p>
                <p class="tw-mt-2 tw-text-sm tw-text-green-800">There's nothing to purchase. Head to My Learning to get started.</p>
                <a :href="myLearningUrl" class="tw-mt-4 tw-inline-block tw-rounded-md tw-bg-green-700 tw-px-4 tw-py-2 tw-font-medium tw-text-white hover:tw-bg-green-800">
                    Go to My Learning
                </a>
                <a
                    v-if="purchaseHistoryUrl"
                    :href="purchaseHistoryUrl"
                    class="tw-mt-4 tw-ml-4 tw-inline-block tw-text-sm tw-font-medium tw-text-green-800 tw-underline hover:tw-text-green-900"
                >
                    View purchase history
                </a>
            </div>

            <!-- Paid already, provisioning still catching up (CORVO-2065) -->
            <div v-else-if="purchaseInProgressMessage" ref="inProgressPanel" tabindex="-1" class="tw-mt-6 tw-rounded-lg tw-border tw-border-green-200 tw-bg-green-50 tw-p-6" role="status">
                <p class="tw-font-medium tw-text-green-900">Your purchase is being set up</p>
                <p class="tw-mt-2 tw-text-sm tw-text-green-800">{{ purchaseInProgressMessage }}</p>
                <a
                    v-if="authenticated"
                    :href="myLearningUrl"
                    class="tw-mt-4 tw-inline-block tw-rounded-md tw-bg-green-700 tw-px-4 tw-py-2 tw-font-medium tw-text-white hover:tw-bg-green-800"
                >
                    Go to My Learning
                </a>
            </div>

            <!-- Purchase flow -->
            <form v-else class="tw-mt-6" novalidate @submit.prevent="onSubmit">
                <!-- Top-level error (aria-live so it's announced) -->
                <div aria-live="assertive" role="alert">
                    <div v-if="errorMessage" class="tw-mb-4 tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-4 tw-text-sm tw-text-red-800">
                        {{ errorMessage }}
                        <template v-if="showLoginLink">
                            <a :href="loginUrl" class="tw-font-semibold tw-underline">Log in to continue.</a>
                            <a v-if="resetPasswordUrl" :href="resetPasswordUrl" class="tw-ml-2 tw-font-semibold tw-underline">Reset your password.</a>
                        </template>
                    </div>
                </div>

                <!-- Guest email -->
                <fieldset v-if="!authenticated" class="tw-mb-6" :disabled="paymentReady">
                    <legend class="tw-mb-2 tw-text-base tw-font-semibold tw-text-gray-900">Your account</legend>
                    <label for="cr-email" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                        Email address <span class="tw-text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="cr-email"
                        v-model="form.email"
                        type="email"
                        name="email"
                        required
                        autocomplete="email"
                        aria-required="true"
                        :aria-invalid="!!fieldErrors.email"
                        :aria-describedby="fieldErrors.email ? 'cr-email-error' : undefined"
                        :class="fieldClass('email')"
                        class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1"
                    >
                    <p id="cr-email-error" v-if="fieldErrors.email" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.email }}</p>
                    <p class="tw-mt-1 tw-text-xs tw-text-gray-500">We'll email you a link to set your password and finish setting up your account.</p>
                </fieldset>

                <!-- Name + phone: collected for guests and members missing them -->
                <fieldset v-if="collectProfile" class="tw-mb-6" :disabled="paymentReady">
                    <legend class="tw-mb-2 tw-text-base tw-font-semibold tw-text-gray-900">Your details</legend>

                    <div class="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                        <div>
                            <label for="cr-first-name" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                First name <span class="tw-text-red-600" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="cr-first-name"
                                v-model="form.firstName"
                                type="text"
                                name="first_name"
                                autocomplete="given-name"
                                required
                                aria-required="true"
                                :aria-invalid="!!fieldErrors.first_name"
                                :aria-describedby="fieldErrors.first_name ? 'cr-first-name-error' : undefined"
                                :class="fieldClass('first_name')"
                                class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1"
                            >
                            <p id="cr-first-name-error" v-if="fieldErrors.first_name" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.first_name }}</p>
                        </div>
                        <div>
                            <label for="cr-last-name" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                Last name <span class="tw-text-red-600" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="cr-last-name"
                                v-model="form.lastName"
                                type="text"
                                name="last_name"
                                autocomplete="family-name"
                                required
                                aria-required="true"
                                :aria-invalid="!!fieldErrors.last_name"
                                :aria-describedby="fieldErrors.last_name ? 'cr-last-name-error' : undefined"
                                :class="fieldClass('last_name')"
                                class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1"
                            >
                            <p id="cr-last-name-error" v-if="fieldErrors.last_name" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.last_name }}</p>
                        </div>
                    </div>

                    <div class="tw-mt-4">
                        <label for="cr-phone" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                            Phone number <span class="tw-text-red-600" aria-hidden="true">*</span>
                        </label>
                        <input
                            id="cr-phone"
                            v-model="form.phone"
                            type="tel"
                            name="phone"
                            autocomplete="tel"
                            maxlength="16"
                            required
                            aria-required="true"
                            :aria-invalid="!!fieldErrors.phone"
                            :aria-describedby="fieldErrors.phone ? 'cr-phone-error' : undefined"
                            :class="fieldClass('phone')"
                            class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1"
                        >
                        <p id="cr-phone-error" v-if="fieldErrors.phone" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.phone }}</p>
                    </div>
                </fieldset>

                <!-- Billing address (drives tax) -->
                <fieldset class="tw-mb-6" :disabled="paymentReady">
                    <legend class="tw-mb-2 tw-text-base tw-font-semibold tw-text-gray-900">Billing address</legend>

                    <div class="tw-grid tw-gap-4">
                        <div>
                            <label for="cr-line1" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                Address line 1 <span class="tw-text-red-600" aria-hidden="true">*</span>
                            </label>
                            <input id="cr-line1" v-model="form.line1" type="text" autocomplete="address-line1" required aria-required="true"
                                   :aria-invalid="!!fieldErrors.line1"
                                   :aria-describedby="fieldErrors.line1 ? 'cr-line1-error' : undefined"
                                   :class="fieldClass('line1')" class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1">
                            <p id="cr-line1-error" v-if="fieldErrors.line1" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.line1 }}</p>
                        </div>
                        <div>
                            <label for="cr-line2" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">Address line 2</label>
                            <input id="cr-line2" v-model="form.line2" type="text" autocomplete="address-line2"
                                   :aria-invalid="!!fieldErrors.line2"
                                   :aria-describedby="fieldErrors.line2 ? 'cr-line2-error' : undefined"
                                   :class="fieldClass('line2')" class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1">
                            <p id="cr-line2-error" v-if="fieldErrors.line2" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.line2 }}</p>
                        </div>
                        <div class="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                            <div>
                                <label for="cr-city" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    City <span class="tw-text-red-600" aria-hidden="true">*</span>
                                </label>
                                <input id="cr-city" v-model="form.city" type="text" autocomplete="address-level2" required aria-required="true"
                                       :aria-invalid="!!fieldErrors.city"
                                       :aria-describedby="fieldErrors.city ? 'cr-city-error' : undefined"
                                       :class="fieldClass('city')" class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1">
                                <p id="cr-city-error" v-if="fieldErrors.city" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.city }}</p>
                            </div>
                            <div>
                                <label for="cr-state" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    State <span class="tw-text-red-600" aria-hidden="true">*</span>
                                </label>
                                <select id="cr-state" v-model="form.state" required aria-required="true"
                                        :aria-invalid="!!fieldErrors.state"
                                        :aria-describedby="fieldErrors.state ? 'cr-state-error' : undefined"
                                        :class="fieldClass('state')" class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-bg-white tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1">
                                    <option value="" disabled>Select…</option>
                                    <option v-for="(name, abbr) in states" :key="abbr" :value="abbr">{{ name }}</option>
                                </select>
                                <p id="cr-state-error" v-if="fieldErrors.state" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.state }}</p>
                            </div>
                        </div>
                        <div class="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                            <div>
                                <label for="cr-postal" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    ZIP / Postal code <span class="tw-text-red-600" aria-hidden="true">*</span>
                                </label>
                                <input id="cr-postal" v-model="form.postal_code" type="text" autocomplete="postal-code" required aria-required="true"
                                       :aria-invalid="!!fieldErrors.postal_code"
                                       :aria-describedby="fieldErrors.postal_code ? 'cr-postal-error' : undefined"
                                       :class="fieldClass('postal_code')" class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1">
                                <p id="cr-postal-error" v-if="fieldErrors.postal_code" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.postal_code }}</p>
                            </div>
                            <div>
                                <label for="cr-country" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    Country <span class="tw-text-red-600" aria-hidden="true">*</span>
                                </label>
                                <select id="cr-country" v-model="form.country" required aria-required="true"
                                        :aria-invalid="!!fieldErrors.country"
                                        :aria-describedby="fieldErrors.country ? 'cr-country-error' : undefined"
                                        :class="fieldClass('country')" class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-bg-white tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-1">
                                    <option v-for="(name, code) in countries" :key="code" :value="code">{{ name }}</option>
                                </select>
                                <p id="cr-country-error" v-if="fieldErrors.country" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.country }}</p>
                            </div>
                        </div>
                    </div>
                </fieldset>

                <div class="tw-mb-6">
                    <div class="tw-flex tw-items-start">
                        <div class="tw-flex tw-h-5 tw-items-center">
                            <input
                                id="cr-terms"
                                v-model="form.terms_of_service_accepted"
                                type="checkbox"
                                name="terms_of_service_accepted"
                                true-value="1"
                                false-value=""
                                required
                                aria-required="true"
                                :aria-invalid="!!fieldErrors.terms_of_service_accepted"
                                :aria-describedby="fieldErrors.terms_of_service_accepted ? 'cr-terms-error' : undefined"
                                :class="fieldErrors.terms_of_service_accepted
                                    ? 'tw-border-red-500 focus:tw-ring-red-500'
                                    : 'tw-border-gray-300 focus:tw-ring-simucase-red'"
                                class="tw-h-4 tw-w-4 tw-rounded tw-text-simucase-red"
                            >
                        </div>
                        <div class="tw-ml-3 tw-text-sm tw-leading-6">
                            <label for="cr-terms" class="tw-font-medium tw-text-gray-700">
                                I agree to the
                                <a href="https://www.simucase.com/terms" target="_blank" rel="noopener" class="tw-text-simucase-red tw-underline hover:tw-text-simucase-red-dark">Simucase Terms and Conditions</a>
                                and
                                <a href="https://www.simucase.com/privacy-policy" target="_blank" rel="noopener" class="tw-text-simucase-red tw-underline hover:tw-text-simucase-red-dark">Privacy Policy</a>
                                <span class="tw-text-red-600" aria-hidden="true">*</span>
                            </label>
                            <p id="cr-terms-error" v-if="fieldErrors.terms_of_service_accepted" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.terms_of_service_accepted }}</p>
                        </div>
                    </div>
                </div>

                <!-- Cloudflare Turnstile (guests only; renders when a site key is configured) -->
                <div v-if="turnstileRequired" class="tw-mb-6">
                    <!-- role + label so this is a named target rather than an
                         anonymous div, and tabindex so a failure can move focus
                         here — the widget itself is a third-party iframe we can't
                         focus into. aria-invalid is deliberately absent: it has no
                         meaning on a container, so the failure is conveyed by the
                         aria-live banner and the description below. -->
                    <div
                        id="cr-captcha"
                        ref="turnstile"
                        role="group"
                        aria-label="Human verification"
                        tabindex="-1"
                        :aria-describedby="fieldErrors.captcha ? 'cr-captcha-error' : undefined"
                    ></div>
                    <p id="cr-captcha-error" v-if="fieldErrors.captcha" class="tw-mt-1 tw-text-sm tw-text-red-600">{{ fieldErrors.captcha }}</p>
                </div>

                <!-- Continue: create the invoice + reveal the Stripe Payment Element -->
                <button v-if="!paymentReady" type="submit" :disabled="creating || (turnstileRequired && !turnstileToken)"
                        class="tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-bg-simucase-red tw-px-4 tw-py-3 tw-font-semibold tw-text-white hover:tw-bg-simucase-red-dark disabled:tw-cursor-not-allowed disabled:tw-opacity-60">
                    <span v-if="creating">Preparing secure payment…</span>
                    <span v-else>Continue to payment</span>
                </button>

                <!-- Stripe Payment Element (mounted once we have a client secret) -->
                <div v-show="paymentReady" class="tw-mt-6">
                    <h2 class="tw-mb-3 tw-text-base tw-font-semibold tw-text-gray-900">Payment details</h2>
                    <div ref="paymentElement" class="tw-rounded-md tw-border tw-border-gray-200 tw-p-3"></div>

                    <button type="submit" :disabled="paying"
                            class="tw-mt-6 tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-bg-simucase-red tw-px-4 tw-py-3 tw-font-semibold tw-text-white hover:tw-bg-simucase-red-dark disabled:tw-cursor-not-allowed disabled:tw-opacity-60">
                        <span v-if="paying">Processing…</span>
                        <span v-else>Pay {{ formattedTotal || formattedPrice }}</span>
                    </button>

                    <p class="tw-mt-3 tw-text-center tw-text-xs tw-text-gray-500">
                        Your card is processed securely by Stripe. Simucase never stores your card details.
                    </p>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import axios from 'axios';

/**
 * CORVO-1638 — Career Readiness purchase (shared by logged-in members and guests).
 *
 * Flow:
 *   1. Collect email (guests) + billing address.
 *   2. POST the purchase endpoint, which creates a finalized one-off invoice
 *      (tax computed from the address via Avalara) and returns its PaymentIntent
 *      client secret.
 *   3. Mount the Stripe Payment Element against that secret and confirm payment.
 *
 * Provisioning happens server-side on the invoice.paid webhook, not here; on
 * success this component just confirms the payment was submitted. Guests receive
 * an activation email to set their password.
 */
export default {
    name: 'CareerReadinessPurchase',

    props: {
        // Injected from Blade; see career-readiness/purchase.blade.php.
        config: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            authenticated: this.config.authenticated,
            alreadyActive: !!(this.config.accountState && this.config.accountState.hasActiveCR),
            // CORVO-2065 sidebar states: the purchaser's own receipt, or a no-price
            // notice for institution-provisioned access.
            activePurchase: this.config.activePurchase || null,
            providedByInstitution: !!this.config.providedByInstitution,
            isDiscounted: this.config.accountState
                ? this.config.accountState.lookupKey === this.config.discountLookupKey
                : false,
            price: this.config.price, // { amount, currency }
            pricing: null,            // { subtotal, tax, total, currency } once the invoice exists
            loginUrl: this.config.loginUrl,
            resetPasswordUrl: this.config.resetPasswordUrl,
            myLearningUrl: this.config.myLearningUrl,
            purchaseHistoryUrl: this.config.purchaseHistoryUrl || '',
            purchaseUrl: this.config.purchaseUrl,
            turnstileSiteKey: this.config.turnstileSiteKey || '',
            turnstileToken: '',
            turnstileWidgetId: null,
            // { ABBR: 'Name' } maps from config('simucase.billing.*') — the same
            // arrays the purchase endpoint allow-lists against (CORVO-2202).
            states: this.config.states || {},
            countries: this.config.countries || {},

            form: {
                email: '',
                firstName: (this.config.profile && this.config.profile.firstName) || '',
                lastName: (this.config.profile && this.config.profile.lastName) || '',
                phone: (this.config.profile && this.config.profile.phone) || '',
                line1: '',
                line2: '',
                city: '',
                state: '',
                postal_code: '',
                country: 'US',
                terms_of_service_accepted: '',
            },

            creating: false,   // POST /purchase in flight
            paying: false,     // stripe.confirmPayment in flight
            paymentReady: false,

            errorMessage: '',
            showLoginLink: false,
            purchaseInProgressMessage: '',
            fieldErrors: {},

            stripe: null,
            elements: null,
        };
    },

    computed: {
        formattedPrice() {
            return this.formatMoney(this.price.amount, this.price.currency);
        },
        cardLabel() {
            if (!this.activePurchase || !this.activePurchase.cardLastFour) {
                return '';
            }
            const brand = this.activePurchase.cardBrand || 'Card';
            return `${brand.charAt(0).toUpperCase()}${brand.slice(1)} ending in ${this.activePurchase.cardLastFour}`;
        },
        collectProfile() {
            // Guests always supply name + phone; a logged-in member is asked only
            // when their profile is missing one of them.
            const p = this.config.profile || {};
            return !this.authenticated || !(p.firstName && p.lastName && p.phone);
        },
        turnstileRequired() {
            // CAPTCHA is a guest-lane control; only render it when a site key is
            // configured (skipped in envs without Turnstile set up).
            return !this.authenticated && !!this.turnstileSiteKey;
        },
        formattedSubtotal() {
            return this.pricing ? this.formatMoney(this.pricing.subtotal, this.pricing.currency) : '';
        },
        formattedTax() {
            return this.pricing ? this.formatMoney(this.pricing.tax, this.pricing.currency) : '';
        },
        formattedTotal() {
            return this.pricing ? this.formatMoney(this.pricing.total, this.pricing.currency) : '';
        },
    },

    mounted() {
        if (this.turnstileRequired) {
            this.renderTurnstile();
        }
    },

    methods: {
        // Border/focus-ring state for a field, so a validation failure is visible
        // and not only announced. The static classes on each control carry
        // everything except the colours; this supplies the colours for both
        // states, rather than layering a red class over the grey one and relying
        // on stylesheet order to win.
        fieldClass(key) {
            return this.fieldErrors[key]
                ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                : 'tw-border-gray-300 focus:tw-border-simucase-red focus:tw-ring-simucase-red';
        },

        // Send keyboard and screen-reader users straight to the first problem
        // rather than making them hunt for it after the banner appears. Ordered
        // to match the visual order of the form, not the server's error bag.
        focusFirstError() {
            const order = [
                ['email', 'cr-email'],
                ['first_name', 'cr-first-name'],
                ['last_name', 'cr-last-name'],
                ['phone', 'cr-phone'],
                ['line1', 'cr-line1'],
                ['line2', 'cr-line2'],
                ['city', 'cr-city'],
                ['state', 'cr-state'],
                ['postal_code', 'cr-postal'],
                ['country', 'cr-country'],
                ['terms_of_service_accepted', 'cr-terms'],
                // Last in the form, and the only one that isn't a real control —
                // the container carries tabindex="-1" so it can still take focus.
                ['captcha', 'cr-captcha'],
            ];
            const first = order.find(([key]) => this.fieldErrors[key]);
            if (! first) {
                return;
            }
            this.$nextTick(() => {
                const el = document.getElementById(first[1]);
                if (el) {
                    el.focus();
                }
            });
        },

        // The success/in-progress panels replace the whole form via v-else-if, so
        // focus would otherwise be sitting on a button that no longer exists and
        // fall back to <body> (WCAG 2.4.3). Move it to the panel that took its
        // place; role="status" then reads the outcome from there.
        focusReplacementPanel(ref) {
            this.$nextTick(() => {
                const el = this.$refs[ref];
                if (el) {
                    el.focus();
                }
            });
        },

        formatMoney(amount, currency) {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: currency || 'USD',
            }).format(Number(amount || 0));
        },

        onSubmit() {
            // Same submit handler drives both stages: create the invoice, then pay.
            if (this.paymentReady) {
                this.confirmPayment();
            } else {
                this.createInvoice();
            }
        },

        renderTurnstile(attempt = 0) {
            // The Turnstile script loads async; wait for it, then render explicitly
            // so we can capture the token for the axios payload (a SPA can't rely on
            // the implicit hidden-input injection a native form submit would get).
            if (!window.turnstile) {
                if (attempt < 40) {
                    setTimeout(() => this.renderTurnstile(attempt + 1), 100);
                }
                return;
            }
            this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
                sitekey: this.turnstileSiteKey,
                callback: (token) => { this.turnstileToken = token; },
                'expired-callback': () => { this.turnstileToken = ''; },
                'error-callback': () => { this.turnstileToken = ''; },
            });
        },

        resetTurnstile() {
            // Tokens are single-use; get a fresh one after any failed attempt.
            this.turnstileToken = '';
            if (this.turnstileWidgetId !== null && window.turnstile) {
                window.turnstile.reset(this.turnstileWidgetId);
            }
        },

        async createInvoice() {
            this.errorMessage = '';
            this.showLoginLink = false;
            this.fieldErrors = {};
            this.creating = true;

            try {
                const { data } = await axios.post(this.purchaseUrl, {
                    email: this.authenticated ? undefined : this.form.email,
                    first_name: this.collectProfile ? this.form.firstName : undefined,
                    last_name: this.collectProfile ? this.form.lastName : undefined,
                    phone: this.collectProfile ? this.form.phone : undefined,
                    line1: this.form.line1,
                    line2: this.form.line2,
                    city: this.form.city,
                    state: this.form.state,
                    postal_code: this.form.postal_code,
                    country: this.form.country,
                    terms_of_service_accepted: this.form.terms_of_service_accepted,
                    'cf-turnstile-response': this.turnstileRequired ? this.turnstileToken : undefined,
                });

                this.pricing = data.pricing;
                await this.mountPaymentElement(data.clientSecret);
                this.paymentReady = true;
            } catch (error) {
                this.handleCreateError(error);
            } finally {
                this.creating = false;
            }
        },

        async mountPaymentElement(clientSecret) {
            // Stripe.js is loaded globally by the checkout layout.
            if (!this.stripe) {
                this.stripe = window.Stripe(this.config.publishableKey);
            }
            this.elements = this.stripe.elements({ clientSecret });
            const paymentElement = this.elements.create('payment', { layout: 'tabs' });
            await this.$nextTick();
            paymentElement.mount(this.$refs.paymentElement);
        },

        async confirmPayment() {
            this.errorMessage = '';
            this.paying = true;

            const { error, paymentIntent } = await this.stripe.confirmPayment({
                elements: this.elements,
                confirmParams: { return_url: this.config.returnUrl },
                redirect: 'if_required',
            });

            if (error) {
                // Card declines / validation: stay on the page and show the message.
                this.errorMessage = error.message || 'We could not process your payment. Please try again.';
                this.paying = false;
                return;
            }

            // Hand off to the confirmation page instead of flipping an inline panel
            // (CORVO-1990): the buyer needs a URL they can reload and a record of
            // what they bought. `redirect: 'if_required'` means we only get here on
            // the no-redirect path, so we build the same query shape Stripe appends
            // on the redirect path and both land on one page.
            const status = paymentIntent && paymentIntent.status;
            if (status === 'succeeded' || status === 'processing') {
                const params = new URLSearchParams({
                    payment_intent: paymentIntent.id,
                    redirect_status: status === 'succeeded' ? 'succeeded' : 'pending',
                });
                window.location.assign(`${this.config.returnUrl}?${params}`);
                return;
            }

            // Any other terminal status (requires_payment_method, canceled, ...):
            // keep them here rather than sending them to a confirmation they have
            // not earned.
            this.errorMessage = 'We could not confirm your payment. Please check your card details and try again.';
            this.paying = false;
        },

        handleCreateError(error) {
            const response = error.response;

            if (response && response.status === 422 && response.data) {
                if (response.data.error === 'captcha_failed') {
                    // Also set errorMessage: the field-level paragraph alone is
                    // outside the aria-live region, so a screen-reader user got no
                    // announcement at all that the attempt had failed.
                    this.fieldErrors.captcha = response.data.message;
                    this.errorMessage = response.data.message;
                    this.resetTurnstile();
                    this.focusFirstError();
                    return;
                }
                if (response.data.error === 'account_exists') {
                    this.errorMessage = response.data.message;
                    this.showLoginLink = true;
                    this.resetTurnstile();
                    return;
                }
                if (response.data.error === 'already_active') {
                    this.alreadyActive = true;
                    this.resetTurnstile();
                    this.focusReplacementPanel('activePanel');
                    return;
                }
                if (response.data.error === 'purchase_in_progress') {
                    // They already paid; provisioning is still catching up. Say so
                    // instead of leaving them on a form that invites a second charge.
                    this.purchaseInProgressMessage = response.data.message;
                    this.resetTurnstile();
                    this.focusReplacementPanel('inProgressPanel');
                    return;
                }
                if (response.data.errors) {
                    // Laravel validation bag. Validation runs before CAPTCHA
                    // verification, so the token was not consumed — keep it.
                    Object.keys(response.data.errors).forEach((key) => {
                        this.fieldErrors[key] = response.data.errors[key][0];
                    });
                    this.errorMessage = 'Please correct the highlighted fields.';
                    this.focusFirstError();
                    return;
                }
            }

            this.errorMessage = 'Something went wrong preparing your payment. Please try again.';
            this.resetTurnstile();
        },
    },
};
</script>
